// src/controllers/context/AuthContext.js
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { supabase } from '../services/supabase';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoginPopupOpen, setIsLoginPopupOpen] = useState(false);
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false);
  const [shortlistedProperties, setShortlistedProperties] = useState([]);
  const awaitingEmailOtpRef = useRef(false);

  // Fetch full user profile from public.users table
  const fetchUserProfile = useCallback(async (userId, email) => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        console.error('Error fetching user profile:', error);
        return null;
      }

      return { ...data, email };
    } catch (e) {
      console.error(e);
      return null;
    }
  }, []);

  // Fetch user favorites (shortlisted properties)
  const fetchUserFavorites = useCallback(async (userId) => {
    try {
      const { data, error } = await supabase
        .from('favorites')
        .select('property_id')
        .eq('user_id', userId);

      if (error) {
        console.error('Error fetching favorites:', error);
        return [];
      }

      return data.map((fav) => fav.property_id);
    } catch (e) {
      console.error(e);
      return [];
    }
  }, []);

  const isOtpVerified = (profile) => profile?.otp_verified !== false;

  const sendEmailOtp = async (email) => {
    const { error } = await supabase.auth.signInWithOtp({
      email: email.trim(),
      options: {
        shouldCreateUser: false,
        emailRedirectTo: `${window.location.origin}/`,
      },
    });
    return error;
  };

  const isAlreadyRegisteredError = (error) => {
    const code = (error?.code || '').toLowerCase();
    const msg = (error?.message || '').toLowerCase();
    return (
      code === 'user_already_exists' ||
      msg.includes('already registered') ||
      msg.includes('already been registered') ||
      msg.includes('user already exists')
    );
  };

  const isEmailNotConfirmedError = (error) => {
    const code = (error?.code || '').toLowerCase();
    const msg = (error?.message || '').toLowerCase();
    return (
      code === 'email_not_confirmed' ||
      msg.includes('email not confirmed') ||
      msg.includes('email_not_confirmed')
    );
  };

  useEffect(() => {
    // 1. Check current session on mount
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        const profile = await fetchUserProfile(session.user.id, session.user.email);
        if (profile && isOtpVerified(profile)) {
          setUser(profile);
          const favs = await fetchUserFavorites(session.user.id);
          setShortlistedProperties(favs);
        } else {
          await supabase.auth.signOut();
          setUser(null);
          setShortlistedProperties([]);
        }
      }
    };
    checkSession();

    // 2. Listen to Auth State Changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        setIsPasswordRecovery(true);
        setIsLoginPopupOpen(true);
      }

      // Don't auto-login while waiting for email OTP after signup
      if (awaitingEmailOtpRef.current) {
        if (!session?.user) {
          setUser(null);
          setShortlistedProperties([]);
        }
        return;
      }

      if (session?.user) {
        const profile = await fetchUserProfile(session.user.id, session.user.email);
        if (profile && isOtpVerified(profile)) {
          setUser(profile);
          const favs = await fetchUserFavorites(session.user.id);
          setShortlistedProperties(favs);
        } else if (!profile || !isOtpVerified(profile)) {
          setUser(null);
          setShortlistedProperties([]);
        }
      } else {
        setUser(null);
        setShortlistedProperties([]);
      }
    });

    return () => {
      subscription?.unsubscribe();
    };
  }, [fetchUserProfile, fetchUserFavorites]);

  const login = useCallback(async (email, password) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) {
        if (isEmailNotConfirmedError(error)) {
          awaitingEmailOtpRef.current = true;
          await supabase.auth.signOut();
          const otpError = await sendEmailOtp(email);
          if (otpError) {
            awaitingEmailOtpRef.current = false;
            return { success: false, error: otpError.message };
          }
          return {
            success: false,
            needsEmailVerification: true,
            email,
            otpType: 'email',
            error: 'Email not verified. We sent a new OTP to your inbox.',
          };
        }
        return { success: false, error: error.message };
      }

      const profile = await fetchUserProfile(data.user.id, data.user.email);
      if (!profile) {
        await supabase.auth.signOut();
        return { success: false, error: 'User profile not found in database.' };
      }

      if (!isOtpVerified(profile)) {
        awaitingEmailOtpRef.current = true;
        await supabase.auth.signOut();
        setUser(null);
        setShortlistedProperties([]);
        const otpError = await sendEmailOtp(email);
        if (otpError) {
          awaitingEmailOtpRef.current = false;
          return { success: false, error: otpError.message };
        }
        return {
          success: false,
          needsEmailVerification: true,
          email,
          otpType: 'email',
          error: 'Email not verified. We sent a new OTP to your inbox.',
        };
      }

      setUser(profile);
      const favs = await fetchUserFavorites(data.user.id);
      setShortlistedProperties(favs);
      setIsLoginPopupOpen(false);
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, [fetchUserProfile, fetchUserFavorites]);

  // Every account starts as a buyer and can sell from the same login, so
  // sign-up no longer takes or stores a role.
  const signup = useCallback(async (email, password, fullName, phoneNumber) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
            phone_number: phoneNumber,
          },
          emailRedirectTo: `${window.location.origin}/`,
        },
      });
      if (error) {
        if (isAlreadyRegisteredError(error)) {
          awaitingEmailOtpRef.current = true;
          const otpError = await sendEmailOtp(email);
          if (otpError) {
            awaitingEmailOtpRef.current = false;
            return {
              success: false,
              error: 'This email is already registered. Please log in.',
            };
          }
          return {
            success: true,
            needsEmailVerification: true,
            email,
            otpType: 'email',
          };
        }
        return {
          success: false,
          error: error.message || 'Registration failed. Please try again.',
        };
      }
      if (!data.user) return { success: false, error: 'Sign up failed.' };

      const targetEmail = data.user.email || email;
      awaitingEmailOtpRef.current = true;

      // Ensure profile exists (trigger also creates it)
      await supabase.from('users').upsert({
        id: data.user.id,
        full_name: fullName,
        phone_number: phoneNumber,
        status: 'active',
        otp_verified: false,
      });

      // Always force OTP before allowing access.
      // If Confirm email is OFF, Supabase returns a session immediately — clear it and send email OTP.
      let otpType = 'signup';
      if (data.session) {
        await supabase.auth.signOut();
        setUser(null);
        setShortlistedProperties([]);
        otpType = 'email';
        const { error: otpError } = await supabase.auth.signInWithOtp({
          email: targetEmail.trim(),
          options: {
            shouldCreateUser: false,
            emailRedirectTo: `${window.location.origin}/`,
          },
        });
        if (otpError) {
          awaitingEmailOtpRef.current = false;
          return {
            success: false,
            error: otpError.message || 'Could not send OTP. Please try again.',
          };
        }
      }

      return {
        success: true,
        needsEmailVerification: true,
        email: targetEmail,
        otpType,
      };
    } catch (err) {
      const message =
        (typeof err?.message === 'string' && err.message.trim()) ||
        'Registration failed. Please try again.';
      return { success: false, error: message };
    }
  }, []);

  const verifySignupOtp = useCallback(async (email, token, otpType = 'signup') => {
    try {
      const type =
        otpType === 'email' ? 'email' : otpType === 'recovery' ? 'recovery' : 'signup';
      const { data, error } = await supabase.auth.verifyOtp({
        email: email.trim(),
        token: String(token || '').trim(),
        type,
      });
      if (error) return { success: false, error: error.message || 'Invalid or expired OTP.' };
      if (!data.user) return { success: false, error: 'Invalid or expired OTP.' };

      if (type === 'recovery') {
        awaitingEmailOtpRef.current = true;
        setIsPasswordRecovery(true);
        return { success: true, needsPasswordReset: true };
      }

      const meta = data.user.user_metadata || {};
      // The account's buying/selling side is left alone here: verifying an
      // email must not flip which side the app opens on.
      await supabase.from('users').upsert({
        id: data.user.id,
        full_name: meta.full_name || null,
        phone_number: meta.phone_number || null,
        status: 'active',
        otp_verified: true,
      });

      awaitingEmailOtpRef.current = false;
      const profile = await fetchUserProfile(data.user.id, data.user.email);
      if (profile) {
        setUser(profile);
        setShortlistedProperties([]);
        setIsLoginPopupOpen(false);
      }
      return { success: true };
    } catch (err) {
      return {
        success: false,
        error: (typeof err?.message === 'string' && err.message) || 'Invalid or expired OTP.',
      };
    }
  }, [fetchUserProfile]);

  const resendSignupOtp = useCallback(async (email, otpType = 'signup') => {
    try {
      if (otpType === 'recovery') {
        const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
          redirectTo: `${window.location.origin}/`,
        });
        if (error) return { success: false, error: error.message };
        return { success: true };
      }

      if (otpType === 'email') {
        const { error } = await supabase.auth.signInWithOtp({
          email: email.trim(),
          options: {
            shouldCreateUser: false,
            emailRedirectTo: `${window.location.origin}/`,
          },
        });
        if (error) return { success: false, error: error.message };
        return { success: true };
      }

      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: email.trim(),
        options: { emailRedirectTo: `${window.location.origin}/` },
      });
      if (error) return { success: false, error: error.message };
      return { success: true };
    } catch (err) {
      return {
        success: false,
        error: (typeof err?.message === 'string' && err.message) || 'Could not resend OTP.',
      };
    }
  }, []);

  const logout = useCallback(async () => {
    await supabase.auth.signOut();
    setUser(null);
    setShortlistedProperties([]);
    setIsPasswordRecovery(false);
  }, []);

  const sendPasswordResetEmail = useCallback(async (email) => {
    try {
      const redirectTo = `${window.location.origin}/`;
      awaitingEmailOtpRef.current = true;
      const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), { redirectTo });
      if (error) {
        awaitingEmailOtpRef.current = false;
        return { success: false, error: error.message };
      }
      return { success: true };
    } catch (err) {
      awaitingEmailOtpRef.current = false;
      return { success: false, error: err.message };
    }
  }, []);

  const updatePassword = useCallback(async (newPassword) => {
    try {
      const { data, error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) return { success: false, error: error.message };
      setIsPasswordRecovery(false);
      awaitingEmailOtpRef.current = false;
      if (data?.user) {
        const profile = await fetchUserProfile(data.user.id, data.user.email);
        if (profile) {
          setUser(profile);
          setShortlistedProperties([]);
          setIsLoginPopupOpen(false);
        }
      }
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, [fetchUserProfile]);

  const toggleShortlist = useCallback(async (propertyId) => {
    if (!user) {
      setIsLoginPopupOpen(true);
      return;
    }

    const isAlreadySaved = shortlistedProperties.includes(propertyId);

    // Optimistic Update
    setShortlistedProperties((prev) =>
      isAlreadySaved ? prev.filter((id) => id !== propertyId) : [...prev, propertyId]
    );

    try {
      if (isAlreadySaved) {
        const { error } = await supabase
          .from('favorites')
          .delete()
          .eq('user_id', user.id)
          .eq('property_id', propertyId);

        if (error) {
          console.error('Error removing from favorites:', error);
          setShortlistedProperties((prev) => [...prev, propertyId]);
        }
      } else {
        const { error } = await supabase
          .from('favorites')
          .insert([{ user_id: user.id, property_id: propertyId }]);

        if (error) {
          console.error('Error adding to favorites:', error);
          setShortlistedProperties((prev) => prev.filter((id) => id !== propertyId));
        }
      }
    } catch (e) {
      console.error(e);
      setShortlistedProperties((prev) =>
        isAlreadySaved ? [...prev, propertyId] : prev.filter((id) => id !== propertyId)
      );
    }
  }, [user, shortlistedProperties]);

  const openLoginPopup = useCallback(() => setIsLoginPopupOpen(true), []);
  const closeLoginPopup = useCallback(() => {
    setIsLoginPopupOpen(false);
    setIsPasswordRecovery(false);
    // If user closes OTP popup without verifying, keep them logged out
    if (awaitingEmailOtpRef.current) {
      awaitingEmailOtpRef.current = false;
      supabase.auth.signOut();
      setUser(null);
      setShortlistedProperties([]);
    }
  }, []);

  const contextValue = useMemo(
    () => ({
      user,
      login,
      signup,
      verifySignupOtp,
      resendSignupOtp,
      logout,
      sendPasswordResetEmail,
      updatePassword,
      isLoginPopupOpen,
      isPasswordRecovery,
      openLoginPopup,
      closeLoginPopup,
      shortlistedProperties,
      toggleShortlist,
    }),
    [
      user,
      login,
      signup,
      verifySignupOtp,
      resendSignupOtp,
      logout,
      sendPasswordResetEmail,
      updatePassword,
      isLoginPopupOpen,
      isPasswordRecovery,
      openLoginPopup,
      closeLoginPopup,
      shortlistedProperties,
      toggleShortlist,
    ]
  );

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};
